import { useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Clock, CheckCircle2 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { INQUIRY_INTERESTS } from "@/lib/inquiryOptions";

const TIME_SLOTS = [
  "9:00 AM", "10:00 AM", "11:00 AM",
  "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM",
];

const TOPICS = INQUIRY_INTERESTS;

export default function Booking() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [date, setDate] = useState();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", time: "", topic: "", message: "",
  });

  const update = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !date || !form.time || !form.topic) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/appointments", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        preferred_date: format(date, "yyyy-MM-dd"),
        preferred_time: form.time,
        topic: form.topic,
        message: form.message,
      });
      toast.success("Request received! Tammy will be in touch within 24 hours.");
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-6 md:px-10 py-32 text-center" data-testid="booking-success">
        <CheckCircle2 className="mx-auto text-primary" size={56} strokeWidth={1.4} />
        <h1 className="font-serif text-5xl mt-8">You're on the calendar.</h1>
        <p className="text-lg text-foreground/75 mt-6 leading-relaxed">
          Tammy will personally review your request and confirm your time by email
          within 24 hours. Look for a note from <strong>trichard@kw.com</strong>.
        </p>
        <Button
          className="mt-10 rounded-full bg-primary hover:bg-primary/90 btn-lift px-8 h-12"
          onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", time: "", topic: "", message: "" }); setDate(undefined); }}
          data-testid="booking-book-another"
        >
          Book another time
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28" data-testid="booking-page">
      <div className="grid md:grid-cols-12 gap-12 items-start">
        <div className="md:col-span-5">
          <span className="text-[11px] uppercase tracking-[0.3em] text-primary">
            Book a Session
          </span>
          <h1 className="font-serif text-5xl md:text-6xl mt-4 leading-[1.02]">
            Let's find time
            <br />
            to talk.
          </h1>
          <p className="mt-8 text-lg text-foreground/80 leading-relaxed">
            Pick a date and time that works for you. Tammy personally reviews
            every request and will confirm your appointment by email within 24
            hours.
          </p>
          <div className="mt-10 p-6 rounded-2xl bg-muted/60 border border-border">
            <p className="text-sm font-medium text-foreground/85">What to expect</p>
            <ul className="mt-3 space-y-2 text-sm text-foreground/70">
              <li>• A relaxed 30–45 minute conversation</li>
              <li>• Honest feedback on where you are right now</li>
              <li>• A clear next step — no pressure to sign up</li>
            </ul>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="md:col-span-7 bg-card border border-border rounded-2xl p-8 md:p-10 space-y-6"
          data-testid="booking-form"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Full name *">
              <Input
                data-testid="booking-name"
                value={form.name}
                onChange={update("name")}
                placeholder="Jane Cooper"
                required
                className="rounded-xl h-11"
              />
            </Field>
            <Field label="Email *">
              <Input
                data-testid="booking-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="jane@example.com"
                required
                className="rounded-xl h-11"
              />
            </Field>
          </div>

          <Field label="Phone (optional)">
            <Input
              data-testid="booking-phone"
              value={form.phone}
              onChange={update("phone")}
              placeholder="(555) 555-5555"
              className="rounded-xl h-11"
            />
          </Field>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Preferred date *">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    type="button"
                    data-testid="booking-date-trigger"
                    className="w-full justify-start rounded-xl h-11 font-normal border-input"
                  >
                    <CalendarIcon className="mr-2" size={16} />
                    {date ? format(date, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </Field>
            <Field label="Preferred time *">
              <Select value={form.time} onValueChange={update("time")}>
                <SelectTrigger className="rounded-xl h-11" data-testid="booking-time-trigger">
                  <Clock className="mr-2 text-foreground/50" size={16} />
                  <SelectValue placeholder="Pick a time" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`booking-time-${t.replace(/[: ]/g, '')}`}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="What would you like to talk about? *">
            <Select value={form.topic} onValueChange={update("topic")}>
              <SelectTrigger className="rounded-xl h-11" data-testid="booking-topic-trigger">
                <SelectValue placeholder="Pick a topic" />
              </SelectTrigger>
              <SelectContent>
                {TOPICS.map((t) => (
                  <SelectItem key={t} value={t} data-testid={`booking-topic-${t.replace(/\s/g,'')}`}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Anything else Tammy should know?">
            <Textarea
              data-testid="booking-message"
              value={form.message}
              onChange={update("message")}
              rows={4}
              placeholder="Where you are in your business, what you're hoping to figure out..."
              className="rounded-xl"
            />
          </Field>

          <Button
            type="submit"
            disabled={submitting}
            data-testid="booking-submit"
            className="w-full rounded-full bg-primary hover:bg-primary/90 btn-lift h-12 text-base"
          >
            {submitting ? "Sending..." : "Request my appointment"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.18em] text-foreground/70 font-medium">
        {label}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
